import { GetDefaultPlayers } from "./utils/playerUtils";

export const GameType = {
  Melee: "melee",
  Ultimate: "ultimate",
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type GameType = typeof GameType[keyof typeof GameType];

export const DraftType = {
  Teams: "teams",
  FFA: "ffa",
  Duel: "duel",
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type DraftType = typeof DraftType[keyof typeof DraftType];

export type DraftData = {
  ID: string;
  Active: boolean;
  DraftType: DraftType;
  GameType: GameType;
  CurrentAction?: Action;
  ActionSequence?: Action[];
  Players: Player[];
  ShopCards: ShopCard[];
  CommonHeroes?: Hero[][];
  Stage?: Stage;
  SelectedPlayers: Player[];
  History: string[];
  TimeDate?: TimeData;
  TournamentGameDetails?: TournamentGameDetails;
  BlueTimer?: number;
  RedTimer?: number;
};

export const draftToTrackData = (draft: DraftData) => {
  const playerTrackData = (player: Player) => {
    let draft: string[] = [];
    player.Draft?.forEach((h) => draft.push(h.Name));
    return {
      "name": player.Name,
      "team": player.Team,
      "number": player.Number,
      "draft": draft,
      "pickedHero": player.PickedHero?.Name,
    };
  };

  let players: any = [];
  draft.Players.forEach((p) => players.push(playerTrackData(p)));

  let shopCards: string[] = [];
  let boughtShopCards: string[] = [];

  draft.ShopCards.forEach((sc) => {
    shopCards.push(sc.Name);
    if (sc.Purchased) {
      boughtShopCards.push(sc.Name);
    }
  });

  return {
    "id": draft.ID,
    "draftType": draft.DraftType,
    "gameType": draft.GameType,
    "players": players,
    "shopCards": shopCards,
    "boughtShopCard": boughtShopCards,
    "stage": draft.Stage?.Name,
    "tournamentDetails": draft.TournamentGameDetails,
  };
};

export function EmptyDraftData(): DraftData {
  const emptyData: DraftData = {
    ID: "",
    Active: false,
    DraftType: DraftType.Teams,
    GameType: GameType.Melee,
    Players: GetDefaultPlayers(),
    ShopCards: [],
    SelectedPlayers: [],
    History: [],
    RedTimer: 0,
    BlueTimer: 0,
  };
  return emptyData;
}

export interface Player {
  Number: number;
  Name: string;
  Team: Team;
  TournamentPlayerNumber?: number;
  Draft?: Hero[];
  PickedHero?: Hero;
  BannedHeroes?: string[];
}

export interface TournamentGameDetails {
  TournamentId: string;
  TournamentName?: string;
  GameId: string;
  GameName?: string;
}

export interface Hero {
  Name: string;
  Image: string;
  Tier: number;
  Banned?: boolean;
  Picked?: boolean;
}

export interface Action {
  Team: Team;
  Action: ActionType;
  PlayerNumber?: number;
  CardEffect?: string;
  Target?: Player;
}

export enum ActionType {
  Pick = "Pick",
  Ban = "Ban",
  Shop = "Shop",
  SelectFriendly = "Select Friendly",
  SelectEnemey = "Select Enemy",
  CardEffect = "CardEffect",
}

export enum Team {
  Blue = "Blue",
  Red = "Red",
}

export interface ShopCard {
  Index: number;
  Name: string;
  Description: string;
  Cost: string;
  Image?: string;
  Effects?: string[];
  Purchased?: Team;
  Frequency?: number;
}

export interface Stage {
  Name: string;
  Image?: string;
}

export interface TimeData {
  CreatedAt: Date;
  TeamBlueCurrentTime: Date;
  TeamBlueReservedTime: Date;
  TeamRedCurrentTime: Date;
  TeamRedReservedTime: Date;
}

export const StagePack = {
  Standard: "standard",
  StageMod: "stageMod",
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type StagePack = typeof StagePack[keyof typeof StagePack];

export type DraftOptions = {
  gameType: GameType;
  showTimer: boolean;
  memeStageChance: number;
  stagePack: StagePack;
};

export const DefaultDraftOptions = (): DraftOptions => {
  return {
    gameType: GameType.Melee,
    showTimer: false,
    memeStageChance: 20,
    stagePack: StagePack.Standard,
  };
};

export const TierConverter = (tierNumber: number): string => {
  switch (tierNumber) {
    case 1:
      return "S";
    case 2:
      return "A";
    case 3:
      return "B+";
    case 4:
      return "B-";
    case 5:
      return "C+";
    case 6:
      return "C-";
    case 7:
      return "D";
    case 8:
      return "F";
    default:
      return "";
  }
};

export const IsSameTierGroup = (tier1: number, tier2: number): boolean => {
  if (TierConverter(tier1)[0] === "D" && TierConverter(tier2)[0] === "F") {
    return true;
  }
  return TierConverter(tier1)[0] === TierConverter(tier2)[0];
};

export const IsSingleDraft = (draftType: DraftType): boolean => {
  return draftType === DraftType.Teams || draftType === DraftType.Duel;
};

export const IsRandomDraft = (draftType: DraftType): boolean => {
  return draftType === DraftType.FFA;
};
